<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { useApiClient } from "@/api/client";
import FileDTO = App.Data.FileDTO;
import { toastApiResult } from "@/toaster";
import router from "@/router/router";
import { useFileFinder } from "@/ui/components/FileBrowser/useFileFinder";
import { useResourceShare } from "@/ui/components/FileBrowser/useResourceShare";
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import AbilityName = App.Enums.AbilityName;
import FileExplorer from "@/ui/components/FileBrowser/FileExplorer.vue";

const resourceTreeCategory = "dashboards";

const breadcrumbs = [
	{
		title: "Dashboard",
		disabled: true,
		to: "/dashboards",
	},
];

const removeDashboardFile = async (file: FileDTO) => {
	return await toastApiResult(useApiClient("dashboard").removeDashboard(file));
};

const batchRemoveDashboardFiles = async (files: FileDTO[]) => {
	return await toastApiResult(
		useApiClient("dashboard").batchRemoveDashboards(files)
	);
};

/** The mode rides along on the file, so creating is a single request. */
const createDashboardFile = async (file: FileDTO) => {
	const result = await toastApiResult(
		useApiClient("dashboard").createDashboard(file)
	);

	if (result.isOk()) {
		await router.push(`/dashboards/${result.value.uuid}`);
	}

	return result;
};

const updateDashboardFileProperties = async (file: FileDTO) => {
	return await toastApiResult(
		useApiClient("dashboard").updateDashboardFromFinder(file)
	);
};

const listUsersWithSharePermissionsOnDashboard = async (resource: FileDTO) => {
	return await useApiClient("dashboard").getUsersWithPermissionsToDashboard(
		resource
	);
};

const grantPermissionsToUserOnDashboard = async (
	resource: FileDTO,
	user: PublicUserDO,
	ability: AbilityName
) => {
	return await useApiClient("dashboard").grantPermissionsToDashboardForUser(
		resource,
		user,
		ability
	);
};

const revokePermissionsForUserOnDashboard = async (
	resource: FileDTO,
	user: PublicUserDO
) => {
	return await useApiClient("dashboard").revokePermissionsToDashboardForUser(
		resource,
		user
	);
};

const listRolesWithSharePermissionsOnDashboard = async (resource: FileDTO) => {
	return await useApiClient("dashboard").getRolesWithPermissionsToDashboard(
		resource
	);
};

const grantPermissionsToRoleOnDashboard = async (
	resource: FileDTO,
	role: RoleDO,
	ability: AbilityName
) => {
	return await useApiClient("dashboard").grantPermissionsToDashboardForRole(
		resource,
		role,
		ability
	);
};

const revokePermissionsForRoleOnDashboard = async (
	resource: FileDTO,
	role: RoleDO
) => {
	return await useApiClient("dashboard").revokePermissionsToDashboardForRole(
		resource,
		role
	);
};

const openFile = (file: FileDTO) => {
	router.push(`dashboards/${file.uuid}`);
};

useFileFinder({
	openFile,
	updateFileProperties: updateDashboardFileProperties,
	createFile: createDashboardFile,
	removeFile: removeDashboardFile,
	batchRemoveFiles: batchRemoveDashboardFiles,
});

useResourceShare(
	listUsersWithSharePermissionsOnDashboard,
	grantPermissionsToUserOnDashboard,
	revokePermissionsForUserOnDashboard,
	listRolesWithSharePermissionsOnDashboard,
	grantPermissionsToRoleOnDashboard,
	revokePermissionsForRoleOnDashboard
);
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />

	<file-explorer
		:resource-tree-category="resourceTreeCategory"
		:actions="[
			'share-file',
			'open-file',
			'delete-file',
			'update-file-meta',
			'new-file-top',
			'new-folder',
			'delete-folder',
			'update-folder',
		]"
	/>
</template>

<style scoped lang="scss">
.finder-height {
	max-height: 70vh;
	height: 70vh;
	margin: 0 0 0 0;
	padding: 0 0 0 0;
}

.psb-h-100 {
	overflow-x: hidden;
	height: 100%;
}
</style>
